import { defineStore } from 'pinia';
import {
  readPersistedStoreState,
  writePersistedStoreState,
} from '@/stores/persistence/pinia-indexeddb';

export type OperationalMapLayerToggleId =
  | 'corridor'
  | 'track'
  | 'rawDeviceTracks'
  | 'routeLines'
  | 'currentPosition'
  | 'stopGeojson'
  | 'stopMarkers'
  | 'exceptions'
  | 'closedExceptions';

/** Legacy single-key snapshot in IndexedDB before per-page scopes. */
export const OPERATIONAL_MAP_LAYERS_LEGACY_STORE_ID = 'operationalMapLayers';

const SCOPED_STORE_PREFIX = `${OPERATIONAL_MAP_LAYERS_LEGACY_STORE_ID}:`;

export const OPERATIONAL_MAP_LAYERS_DEFAULT_SCOPE_KEY = 'shipments-list';

function defaultLayerVisibility(): Record<
  OperationalMapLayerToggleId,
  boolean
> {
  return {
    corridor: true,
    track: true,
    rawDeviceTracks: false,
    routeLines: true,
    currentPosition: true,
    stopGeojson: true,
    stopMarkers: true,
    exceptions: true,
    closedExceptions: false,
  };
}

/** Migrate persisted payload from legacy `paths` (single toggle for both layers). */
function normalizePersistedLayerVisibility(
  raw: Record<string, boolean> | undefined,
): Record<OperationalMapLayerToggleId, boolean> {
  const next = defaultLayerVisibility();
  if (!raw || typeof raw !== 'object') {
    return next;
  }

  const legacyPaths = typeof raw.paths === 'boolean' ? raw.paths : undefined;

  next.corridor =
    typeof raw.corridor === 'boolean' ? raw.corridor : next.corridor;
  next.track =
    typeof raw.track === 'boolean'
      ? raw.track
      : legacyPaths !== undefined
        ? legacyPaths
        : next.track;
  // Missing key → false (debug layer; do not inherit legacy `paths`).
  next.rawDeviceTracks =
    typeof raw.rawDeviceTracks === 'boolean' ? raw.rawDeviceTracks : false;
  next.routeLines =
    typeof raw.routeLines === 'boolean'
      ? raw.routeLines
      : legacyPaths !== undefined
        ? legacyPaths
        : next.routeLines;
  next.currentPosition =
    typeof raw.currentPosition === 'boolean'
      ? raw.currentPosition
      : next.currentPosition;
  next.stopGeojson =
    typeof raw.stopGeojson === 'boolean' ? raw.stopGeojson : next.stopGeojson;
  next.stopMarkers =
    typeof raw.stopMarkers === 'boolean' ? raw.stopMarkers : next.stopMarkers;
  next.exceptions =
    typeof raw.exceptions === 'boolean' ? raw.exceptions : next.exceptions;
  // Missing key → false (do not inherit from `exceptions`).
  next.closedExceptions =
    typeof raw.closedExceptions === 'boolean' ? raw.closedExceptions : false;

  return next;
}

export function normalizeOperationalMapLayersPersistenceKey(
  raw: string,
): string {
  const s = raw.trim();
  if (!s) {
    return OPERATIONAL_MAP_LAYERS_DEFAULT_SCOPE_KEY;
  }
  return s.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 64);
}

function scopedStoreId(scopeKey: string): string {
  return `${SCOPED_STORE_PREFIX}${scopeKey}`;
}

type PersistedLayersPayload = {
  layerVisibility?: Record<string, boolean>;
};

export const useOperationalMapLayersStore = defineStore(
  'operationalMapLayers',
  {
    state: () => ({
      /** Active IndexedDB scope; drives read/write in {@link activatePersistenceScope}. */
      persistenceScopeKey: OPERATIONAL_MAP_LAYERS_DEFAULT_SCOPE_KEY,
      layerVisibility: defaultLayerVisibility(),
    }),
    actions: {
      /**
       * Load layer toggles for this scope from IndexedDB (or defaults).
       * Call when the operational map mounts or when `layersPersistenceKey` changes.
       */
      async activatePersistenceScope(rawKey: string) {
        const scopeKey = normalizeOperationalMapLayersPersistenceKey(rawKey);
        this.persistenceScopeKey = scopeKey;

        const id = scopedStoreId(scopeKey);
        let payload = await readPersistedStoreState<PersistedLayersPayload>(id);

        if (!payload?.layerVisibility) {
          const legacy = await readPersistedStoreState<PersistedLayersPayload>(
            OPERATIONAL_MAP_LAYERS_LEGACY_STORE_ID,
          );
          if (
            legacy?.layerVisibility &&
            scopeKey === OPERATIONAL_MAP_LAYERS_DEFAULT_SCOPE_KEY
          ) {
            payload = legacy;
            const normalized = normalizePersistedLayerVisibility(
              legacy.layerVisibility,
            );
            void writePersistedStoreState(id, {
              layerVisibility: { ...normalized },
            });
          }
        }

        if (payload?.layerVisibility) {
          this.layerVisibility = normalizePersistedLayerVisibility(
            payload.layerVisibility,
          );
        } else {
          this.layerVisibility = defaultLayerVisibility();
        }
      },
      persistToBrowserDb() {
        const normalized = normalizePersistedLayerVisibility(
          this.layerVisibility,
        );
        void writePersistedStoreState(scopedStoreId(this.persistenceScopeKey), {
          layerVisibility: { ...normalized },
        });
      },
      setLayer(id: OperationalMapLayerToggleId, value: boolean) {
        if (id in this.layerVisibility) {
          this.layerVisibility[id] = value;
          this.persistToBrowserDb();
        }
      },
    },
  },
);
